import { useCallback, useEffect, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Divider,
  styled,
} from "@mui/material";
import {
  Close as CloseIcon,
  Bolt as BoltIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  isAdminRole,
  useActiveBalance,
  useCreditStore,
  useCreditWorkspace,
} from "ui";
import { useLoginState } from "@/studioxr/auth/LoginState";
import { useMattoState } from "@/MattoState";
import { UserProfileRole } from "ts-interfaces";
import { MONTHLY_PRO_CREDITS, ANNUAL_PRO_CREDITS } from "@/constants/proCredits";
import { trackEvent } from "@/libs/analyticsManager";

const ModalContainer = styled(Box)(() => ({
  width: "90%",
  maxWidth: "450px",
  maxHeight: "70vh",
  margin: "auto",
  marginTop: "15vh",
  background: "white",
  borderRadius: "12px",
  boxShadow: "0px 16px 45px rgba(15, 23, 42, 0.16)",
  overflow: "auto",
  position: "relative",
}));

const BalanceCard = styled(Box)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(2),
  color: "white",
  textAlign: "center",
  marginBottom: theme.spacing(2),
}));

const BuyCreditsButton = styled(Button)(({ theme }) => ({
  background: "linear-gradient(135deg, #C68CD6 0%, #A869B8 100%)",
  color: "white",
  padding: theme.spacing(1.5, 3),
  fontWeight: "bold",
  fontSize: "1rem",
  borderRadius: theme.spacing(1),
  textTransform: "none",
  "&:hover": {
    background: "linear-gradient(135deg, #B57BC5 0%, #9758A7 100%)",
  },
  "&:disabled": {
    opacity: 0.6,
    color: "white",
  },
}));

interface CreditDetailsModalProps {
  open: boolean;
  onClose: () => void;
  onBuyCredits?: () => void;
}

export const CreditDetailsModal = ({
  open,
  onClose,
  onBuyCredits,
}: CreditDetailsModalProps) => {
  const fetchBalance = useCreditStore((s) => s.fetchBalance);
  const fetchTeamBalance = useCreditStore((s) => s.fetchTeamBalance);
  const profile = useLoginState((state) => state.profile);
  const permissions = useLoginState((state) => state.permissions);
  const setShowSubscription = useMattoState(
    (state) => state.setShowSubscription,
  );

  // Credit workspace: the active workspace when workspaces are enabled, the
  // team wallet for team designers when the feature is gated (see
  // useCreditWorkspace).
  const activeWorkspace = useCreditWorkspace(profile?.uid ?? null, permissions);
  const {
    balance: effectiveBalance,
    isLoading,
    error,
    hideBalance,
  } = useActiveBalance(activeWorkspace);

  // Purchase gate keyed on the ACTIVE (credit) workspace — NOT membership-wide —
  // so the Personal workspace shows the personal Upgrade-to-PRO CTA, not the
  // team-credits one. Matches the wallet whose balance is shown above.
  const isTeamMember = activeWorkspace.kind === "team";
  const isTeamAdmin = isAdminRole(
    activeWorkspace.kind === "team" ? activeWorkspace.role : null,
  );
  const isPro = profile?.userRole === UserProfileRole.PRO;
  const canPurchaseCredits = isTeamMember ? isTeamAdmin : isPro;

  const refreshBalance = useCallback(
    (force = false) => {
      if (activeWorkspace.kind === "team") {
        fetchTeamBalance(activeWorkspace.firmId, force);
        return;
      }
      fetchBalance(force);
    },
    [activeWorkspace.id, activeWorkspace.kind, fetchBalance, fetchTeamBalance],
  );

  useEffect(() => {
    if (open) {
      refreshBalance();
    }
  }, [open, refreshBalance]);

  const credits = effectiveBalance?.totalBalance ?? 0;
  const detailsOpenedTrackedRef = useRef(false);

  useEffect(() => {
    if (open && !detailsOpenedTrackedRef.current) {
      detailsOpenedTrackedRef.current = true;
      trackEvent("Credits - Details Opened", {
        balance: credits,
        canPurchaseCredits,
        isTeamMember,
      });
    }
    if (!open) {
      detailsOpenedTrackedRef.current = false;
    }
  }, [open, credits, canPurchaseCredits, isTeamMember]);

  const handleBuyCredits = () => {
    if (onBuyCredits) {
      onBuyCredits();
      onClose();
    }
  };

  const handleRefresh = () => {
    trackEvent("Credits - Refresh Balance", { balance: credits });
    refreshBalance(true);
  };

  const handleUpgradeToProClick = () => {
    trackEvent("Credits - Upgrade to PRO Click", { source: "details_modal" });
    onClose();
    setShowSubscription("startTrial");
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer>
        <Box sx={{ p: 2.5 }}>
          {/* Header */}
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Stack direction="row" sx={{ alignItems: "center", gap: 0.75 }}>
              <BoltIcon sx={{ color: "#F59E0B", fontSize: "1.5rem" }} />
              <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                Credits
              </Typography>
            </Stack>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          {/* Balance Card */}
          <BalanceCard>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Balance
            </Typography>
            <Stack
              direction="row"
              sx={{ alignItems: "center", justifyContent: "center", my: 1 }}
            >
              {/* Invisible spacer to balance the refresh icon */}
              <Box sx={{ width: 24, visibility: "hidden" }} />
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                {isLoading ? "..." : credits}
              </Typography>
              <IconButton
                onClick={handleRefresh}
                disabled={isLoading}
                size="small"
                sx={{
                  color: "white",
                  opacity: 0.8,
                  ml: 0.5,
                  "&.Mui-disabled": { color: "white", opacity: 0.5 },
                }}
              >
                <RefreshIcon sx={{ fontSize: "1.2rem" }} />
              </IconButton>
            </Stack>
          </BalanceCard>

          {/* Error message — only show if we have no cached balance */}
          {error && !effectiveBalance && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: "#FEF2F2",
                borderRadius: 1,
                border: "1px solid #FEE2E2",
              }}
            >
              <Typography variant="body2" color="#DC2626">
                Failed to load balance
              </Typography>
            </Box>
          )}

          {/* Actions */}
          <Stack spacing={2}>
            {canPurchaseCredits ? (
              <BuyCreditsButton fullWidth onClick={handleBuyCredits}>
                {isTeamMember ? "Buy Team Credits" : "Buy More Credits"}
              </BuyCreditsButton>
            ) : isTeamMember ? (
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#FEF3C7",
                  borderRadius: 1,
                  border: "1px solid #FDE68A",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body2"
                  color="#92400E"
                  sx={{ fontWeight: 500 }}
                >
                  Only team admins can purchase credits for the team.
                </Typography>
                <Typography
                  variant="caption"
                  color="#92400E"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  Please contact your team admin to add more credits.
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#F3E8FF",
                  borderRadius: 1,
                  border: "1px solid #E9D5FF",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body2"
                  color="#6B21A8"
                  sx={{ fontWeight: 500 }}
                >
                  <Box
                    component="span"
                    onClick={handleUpgradeToProClick}
                    sx={{
                      textDecoration: "underline",
                      cursor: "pointer",
                      fontWeight: 700,
                      "&:hover": { color: "#5B21B6" },
                    }}
                  >
                    Upgrade to PRO
                  </Box>
                  {" "}to purchase additional credits.
                </Typography>
                <Typography
                  variant="caption"
                  color="#6B21A8"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  Your free credits are included with your current plan.
                </Typography>
              </Box>
            )}
          </Stack>

          <Divider sx={{ my: 2 }} />

          {/* Info Section */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: "bold", mb: 1, color: "text.secondary" }}
            >
              How Credits Work
            </Typography>
            <Box
              component="ul"
              sx={{ m: 0, pl: 2.5, "& li": { mb: 0.5 }, "& li:last-child": { mb: 0 } }}
            >
              <Typography component="li" variant="caption" color="text.secondary">
                1 concept = 1 credit
              </Typography>
              <Typography component="li" variant="caption" color="text.secondary">
                1 image edit = 1 credit
              </Typography>
              <Typography component="li" variant="caption" color="text.secondary">
                Upgrade to PRO to get additional credits that rollover automatically
                <Box component="ul" sx={{ m: 0, pl: 2, "& li": { mb: 0.25 } }}>
                  <Typography component="li" variant="caption" color="text.secondary">
                    Monthly PRO gives you {MONTHLY_PRO_CREDITS} credits per month
                  </Typography>
                  <Typography component="li" variant="caption" color="text.secondary">
                    Annual PRO gives you {ANNUAL_PRO_CREDITS} credits upfront
                  </Typography>
                </Box>
              </Typography>
              <Typography component="li" variant="caption" color="text.secondary">
                Purchase additional credit bundles as needed
              </Typography>
              <Typography component="li" variant="caption" color="text.secondary">
                Recent usage may take a few minutes to reflect in your balance
              </Typography>
            </Box>
          </Box>
        </Box>
      </ModalContainer>
    </Modal>
  );
};
